import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { BalancePoint } from '../../types/portfolio';
import { fetchBalances } from '../../services/blockchainService';

interface BalanceChartProps {
  userId: string;
}

function formatUSD(value: number) {
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function BalanceChart({ userId }: BalanceChartProps) {
  const [data, setData] = useState<BalancePoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const balances = await fetchBalances(userId);
        if (!cancelled) setData(balances);
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Failed to load balance history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const interval = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [userId]);

  if (loading && !data.length) {
    return <div className="chart-loading">Loading balance history…</div>;
  }

  if (error) {
    return <div className="chart-error">{error}</div>;
  }

  const latest = data[data.length - 1]?.balance ?? 0;

  return (
    <div className="balance-chart">
      <div className="chart-header">
        <h3 className="chart-title">Account Balance</h3>
        <span className="chart-current">{formatUSD(latest)}</span>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          <XAxis
            dataKey="date"
            tick={{ fill: '#8b9abf', fontSize: 11 }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tickFormatter={v => `$${(v / 1000).toFixed(0)}k`}
            tick={{ fill: '#8b9abf', fontSize: 11 }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            formatter={(val: ValueType | undefined) => typeof val === 'number' ? [formatUSD(val), 'Balance'] : []}
            contentStyle={{ background: '#1a2235', border: '1px solid #2d3748', borderRadius: 8 }}
            labelStyle={{ color: '#8b9abf' }}
            itemStyle={{ color: '#f0f4ff' }}
          />
          <Line
            type="monotone"
            dataKey="balance"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, fill: '#3b82f6' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
